import createActivityTracker from './activity';
import createHeartbeat from './heartbeat';
import createPresenceSocketClient from './socketClient';
import getDeviceInfo from './device';

// Configurable via env; fall back to sane defaults.
const HEARTBEAT_INTERVAL =
  Number(process.env.REACT_APP_PRESENCE_HEARTBEAT_MS) || 10_000; // keep under server prune timeout
const HEARTBEAT_JITTER = 3_000;
const LATENCY_INTERVAL =
  Number(process.env.REACT_APP_PRESENCE_LATENCY_MS) || 5_000;
const IDLE_TIMEOUT =
  Number(process.env.REACT_APP_PRESENCE_IDLE_MS) || 180_000; // 3 minutes
const SNAPSHOT_RECONCILE_INTERVAL =
  Number(process.env.REACT_APP_PRESENCE_SNAPSHOT_MS) || 60_000;

const CHANNEL_NAME = 'cube_presence_bc';
const IS_MOBILE =
  typeof navigator !== 'undefined' &&
  /iphone|ipad|android|mobile|ipad|ipod/i.test(navigator.userAgent || '');

// Harmonize with backend expectations
const FRONTEND_IDLE_ON_BLUR = true;
const FRONTEND_ONLINE_ON_FOCUS = true;

export function initPresence(
  socket,
  playerId,
  {
    onPresenceEvent,
    onSnapshot,
    onDisconnect,
    getLatencyMs,
    getActivityType,
    player, // optional player object carrying profilePhoto/handle
  } = {},
) {
  if (!socket || !playerId) return () => {};

  const deviceInfo = getDeviceInfo();
  const presenceClient = createPresenceSocketClient(socket);
  const activityTracker = createActivityTracker({ idleMs: IDLE_TIMEOUT });
  let identifySent = false;
  let connected = !!socket?.connected;

  const playerMeta =
    (player && player.data && player.data.player) ||
    player ||
    (typeof window !== 'undefined' && window.__jg?.self) ||
    {};

  let state = {
    status: 'online', // online | idle | away | offline
    lastActivity: Date.now(),
    hiddenSince: null,
    leader: false,
    stopped: false,
    latencyMs: null, // last known network latency (ms)
    activityType: activityTracker.getActivity().activityType,
  };

  const bc = new BroadcastChannel(CHANNEL_NAME);

  // Unique tab id for leader election
  const tabId = `tab-${Math.random().toString(36).slice(2)}`;

  // Leader election: lowest timestamp tab (or first) becomes leader
  bc.postMessage({ type: 'HELLO', ts: Date.now(), playerId });
  let leaderPingTimeout;
  let leaderCheckTimeout;

  function becomeLeader() {
    if (state.leader) return;
    state.leader = true;
    emitIdentify();
    ensureLatencyLoop();
    sendPresence('presence:join');
    heartbeat.start();
  }

  function resignLeadership() {
    state.leader = false;
    stopLatencyLoop();
    heartbeat.stop();
  }

  bc.onmessage = (e) => {
    const msg = e.data;
    if (!msg) return;

    if (msg.type === 'HELLO' && msg.playerId === playerId) {
      // Decide leader: earliest ts keeps; newer yields
      if (!state.leader) {
        bc.postMessage({ type: 'LEADER_QUERY', ts: Date.now(), playerId });
        leaderCheckTimeout = setTimeout(() => {
          if (!state.leader) becomeLeader(); // nobody asserted leadership
        }, 800);
      }
    } else if (msg.type === 'IM_LEADER' && msg.playerId === playerId) {
      // Another tab is leader
      if (msg.tabId !== tabId) resignLeadership();
    } else if (
      msg.type === 'LEADER_QUERY' &&
      state.leader &&
      msg.playerId === playerId
    ) {
      bc.postMessage({ type: 'IM_LEADER', playerId, tabId });
    } else if (msg.type === 'ACTIVITY' && msg.playerId === playerId) {
      state.lastActivity = Date.now();
      if (state.status === 'idle') updateStatus('online');
    }
  };

  // ----------------------------------------
  // Dual Focus Detection (FAANG-grade)
  // ----------------------------------------

  function isPageVisible() {
    return document.visibilityState === 'visible';
  }

  function goOnline(reason = 'focus') {
    if (state.status !== 'online') {
      updateStatus('online');
      console.log(`[presenceManager] ONLINE ← (${reason})`);
    }
  }

  function goIdle(reason = 'blur') {
    if (state.status !== 'idle') {
      updateStatus('idle');
      console.log(`[presenceManager] IDLE ← (${reason})`);
    }
  }

  //
  // A) window.focus fires ~90% of the time
  //
  function handleFocus() {
    if (FRONTEND_ONLINE_ON_FOCUS) {
      goOnline('window.focus');
      forceStatus('online', 'active'); // immediate emit for fast flip
      sendPresence('presence:heartbeat');
      forceReidentify();
    }
  }

  //
  // B) window.blur always fires immediately
  //
  function handleBlur() {
    if (FRONTEND_IDLE_ON_BLUR) {
      goIdle('window.blur');
      forceStatus('idle', 'background'); // immediate emit for fast flip
      sendPresence('presence:heartbeat');
    }
  }

  //
  // C) visibilitychange fires 100% reliably
  //
  function handleVisibility() {
    if (document.visibilityState === 'visible') {
      goOnline('visibilitychange');
      forceStatus('online', 'active');
      sendPresence('presence:heartbeat');
      forceReidentify();
    } else {
      goIdle('visibilitychange');
      forceStatus('idle', 'background');
      sendPresence('presence:heartbeat');
    }
  }

  //
  // D) 750ms fallback poll
  //
  const focusPoll = setInterval(() => {
    if (document.hasFocus() && isPageVisible()) {
      goOnline('poll');
      forceStatus('online', 'active');
    }
  }, IS_MOBILE ? 1500 : 750);

  //
  // Register listeners
  //
  window.addEventListener('focus', handleFocus);
  window.addEventListener('blur', handleBlur);
  document.addEventListener('visibilitychange', handleVisibility);
  window.addEventListener('click', handleFocus, true); // capture click to force online

  //
  // Store cleanup handlers for return cleanup below
  //
  const removeDualFocus = () => {
    clearInterval(focusPoll);
    window.removeEventListener('focus', handleFocus);
    window.removeEventListener('blur', handleBlur);
    document.removeEventListener('visibilitychange', handleVisibility);
    window.removeEventListener('click', handleFocus, true);
  };

  // Heartbeat logic
  const heartbeat = createHeartbeat({
    intervalMs: HEARTBEAT_INTERVAL,
    jitterMs: HEARTBEAT_JITTER,
    send: () => {
      if (!state.leader || state.stopped || !identifySent || !connected) return;
      sendPresence('presence:heartbeat');
      detectIdle();
    },
  });

  const resolveLatency = () => {
    const externalLatency =
      typeof getLatencyMs === 'function' ? getLatencyMs() : null;
    if (typeof externalLatency === 'number') {
      state.latencyMs = externalLatency;
    }
    return state.latencyMs;
  };

  const activityUnsub = activityTracker.subscribe((activityType, ts) => {
    state.activityType = activityType;
    state.lastActivity = ts || Date.now();
    if (activityType === 'active' && state.status !== 'online') {
      updateStatus('online');
    } else if (activityType === 'idle' && state.status === 'online') {
      updateStatus('idle');
    } else if (activityType === 'background') {
      updateStatus('away');
    }
  });

  let latencyCleanup;

  const ensureLatencyLoop = () => {
    if (latencyCleanup) return;
    latencyCleanup = presenceClient.startLatencyPing(LATENCY_INTERVAL, {
      onLatency: (ms) => {
        state.latencyMs = ms;
      },
    });
  };

  const stopLatencyLoop = () => {
    if (latencyCleanup) {
      latencyCleanup();
      latencyCleanup = null;
    }
  };

  function buildPresencePayload(extra = {}) {
    const latencyMs = resolveLatency();
    const activityType =
      state.activityType ||
      (typeof getActivityType === 'function' ? getActivityType() : null);
    const displayHandle =
      playerMeta.playerId || playerMeta.handle || playerId || null;
    const profilePhoto =
      playerMeta.profilePhoto || playerMeta.avatar || playerMeta.photo || null;

    return {
      playerId, // HANDLE (JG convention)
      handle: displayHandle,
      displayName: displayHandle,
      profilePhoto,
      status: state.status,
      latencyMs,
      activityType,
      device: deviceInfo.deviceType,
      deviceInfo,
      tabId,
      ...extra,
    };
  }

  function emitIdentify() {
    if (identifySent || !connected) return;
    try {
      presenceClient.emitIdentify(buildPresencePayload({ status: 'online' }));
      identifySent = true;
      heartbeat.start();
    } catch {
      /* ignore */
    }
  }

  function sendPresence(event, extra = {}) {
    try {
      socket.emit(event, buildPresencePayload(extra));
    } catch {
      // swallow, presence is best-effort
    }
  }

  function updateStatus(next) {
    if (state.status === next) return;
    state.status = next;
    if (state.leader) sendPresence('presence:update', { status: next });
  }

  function forceStatus(status, activityType) {
    state.status = status;
    if (activityType) state.activityType = activityType;
    // Ensure we have an active session
    emitIdentify();
    sendPresence('presence:update', {
      status,
      activityType: activityType || state.activityType,
    });
  }

  function detectIdle() {
    if (
      Date.now() - state.lastActivity > IDLE_TIMEOUT &&
      state.status === 'online'
    ) {
      updateStatus('idle');
    }
  }

  // Visibility (away)
  function handleVisibilityAway() {
    if (document.hidden) {
      state.hiddenSince = Date.now();
      setTimeout(() => {
        if (document.hidden && state.status !== 'away') updateStatus('away');
      }, 0);
    } else {
      state.hiddenSince = null;
      // Restore from away -> online if user visible & active
      if (state.status === 'away' || state.status === 'idle') {
        updateStatus('online');
      }
    }
  }

  // Activity listeners
  const activityEvents = [
    'mousemove',
    'keydown',
    'click',
    'touchstart',
    'scroll',
  ];
  function markActive() {
    state.lastActivity = Date.now();
    state.activityType = 'interacting';
    if (state.status === 'idle' || state.status === 'away')
      updateStatus('online');
    bc.postMessage({ type: 'ACTIVITY', playerId });
  }

  activityEvents.forEach((ev) =>
    window.addEventListener(ev, markActive, { passive: true }),
  );

  // Fast status switching
  function handleFastFocus() {
    // Only leader pushes updates
    if (!state.leader || state.stopped) return;

    state.lastActivity = Date.now();
    if (state.status !== 'online') {
      console.log('[presenceManager] FAST FOCUS → online');
      forceStatus('online', 'active'); // triggers socket.emit("presence:update")
      forceReidentify();
    }
  }

  function handleFastBlur() {
    if (!state.leader || state.stopped) return;

    // Treat blur as immediate idle
    if (state.status !== 'idle' && state.status !== 'away') {
      console.log('[presenceManager] FAST BLUR → idle');
      forceStatus('idle', 'background'); // triggers socket.emit("presence:update")
      sendPresence('presence:heartbeat');
    }
  }

  window.addEventListener('focus', handleFastFocus);
  window.addEventListener('blur', handleFastBlur);

  document.addEventListener('visibilitychange', handleVisibilityAway);

  // Socket listeners for presence diffs & snapshot
  const diffHandler = (payload) => onPresenceEvent && onPresenceEvent(payload);
  socket.on('presence:diff', diffHandler);

  const snapshotHandler = (payload) => onSnapshot && onSnapshot(payload);
  socket.on('presence:snapshot', snapshotHandler);

  // Reconcile periodically (ask server for snapshot)
  const requestSnapshot = () => {
    if (state.leader) sendPresence('presence:snapshot:request');
  };

  const snapshotInterval = setInterval(requestSnapshot, SNAPSHOT_RECONCILE_INTERVAL);

  const restartPresenceCycle = () => {
    identifySent = false;
    stopLatencyLoop();
    heartbeat.stop();
    emitIdentify();
    ensureLatencyLoop();
  };

  const forceReidentify = () => {
    identifySent = false;
    emitIdentify();
    ensureLatencyLoop();
  };

  // Connection handling
  socket.on('disconnect', () => {
    connected = false;
    identifySent = false;
    heartbeat.stop();
    stopLatencyLoop();
    onDisconnect && onDisconnect();
  });

    // Guard: if backend tells us the session is unknown/rejected, re-identify immediately
    const handlePresenceSessionReset = () => {
      restartPresenceCycle();
    };
    socket.on('presence:heartbeat:reject', handlePresenceSessionReset);
    socket.on('presence:session:unknown', handlePresenceSessionReset);
    socket.on('presence:session:reset', handlePresenceSessionReset);

  // Become leader after short grace if no leader found
  leaderPingTimeout = setTimeout(() => {
    becomeLeader();
    requestSnapshot(); // request immediate snapshot on leader assertion
    emitIdentify();
    bc.postMessage({ type: 'IM_LEADER', playerId, tabId });
  }, 500);

  // Fast unload notification
  function handleUnload() {
    state.stopped = true;
    if (state.leader) {
      try {
        const data = JSON.stringify({ playerId, status: 'offline' });
        navigator.sendBeacon?.('/presence/beacon-leave', data);
        socket.emit('presence:leave', { playerId });
      } catch {
        // ignore
      }
    }
  }

  window.addEventListener('beforeunload', handleUnload);
  window.addEventListener('unload', handleUnload);

  // Initial identify
  emitIdentify();

  // Re-identify on connect/reconnect
  const handleConnect = () => {
    connected = true;
    identifySent = false;
    emitIdentify();
    ensureLatencyLoop();
  };
  socket.on('connect', handleConnect);

  return () => {
    state.stopped = true;
    heartbeat.stop();
    clearTimeout(leaderPingTimeout);
    clearTimeout(leaderCheckTimeout);
    clearInterval(snapshotInterval);
    removeDualFocus();
    window.removeEventListener('focus', handleFastFocus);
    window.removeEventListener('blur', handleFastBlur);

    window.removeEventListener('beforeunload', handleUnload);
    window.removeEventListener('unload', handleUnload);
    document.removeEventListener('visibilitychange', handleVisibilityAway);
    activityEvents.forEach((ev) => window.removeEventListener(ev, markActive));
    activityUnsub?.();
    stopLatencyLoop();
    activityTracker.stop();
    bc.close();
    socket.off('presence:diff', diffHandler);
    socket.off('presence:snapshot', snapshotHandler);
    socket.off('connect', handleConnect);
    socket.off('presence:heartbeat:reject', handlePresenceSessionReset);
    socket.off('presence:session:unknown', handlePresenceSessionReset);
    socket.off('presence:session:reset', handlePresenceSessionReset);
    if (state.leader) socket.emit('presence:leave', { playerId });
  };
}

/**
 * Helper: derive an "online friends" list from a presence map.
 *
 * @param {Object<string, Object>} presenceByMongoId
 *        Map: mongoId -> { status, quality, latencyMs, lastSeen }
 * @param {Array<Object|string>} friends
 *        Either:
 *          - Array of mongoId strings, or
 *          - Array of friend objects with _id/mongoId and optional metadata
 * @returns {Array<{ id: string, presence: Object, friend: Object|null }>}
 */
export function selectOnlineFriends(presenceMap = {}, friends = []) {
  return friends
    .map((friend) => {
      if (!friend || !friend.playerId) return null;

      const key = friend.playerId; // use playerId, not _id
      const presence = presenceMap[key] || null;

      if (!presence || presence.status === 'offline') return null;

      return {
        ...friend,
        presence,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      const rank = (s) =>
        s === 'online'
          ? 0
          : s === 'idle'
          ? 1
          : s === 'away'
          ? 2
          : s === 'dnd'
          ? 3
          : 4;

      const ra = rank(a.presence?.status);
      const rb = rank(b.presence?.status);

      if (ra !== rb) return ra - rb;

      return (a.presence.latencyMs ?? 9999) - (b.presence.latencyMs ?? 9999);
    });
}

/**
 * Helper: choose a ring color token for presence quality.
 * You can map this to CSS classes or inline styles in OrbitingFriends.
 */
export function getPresenceRingColor(quality) {
  switch (quality) {
    case 'green':
      return '#00e676'; // good
    case 'yellow':
      return '#ffeb3b'; // medium
    case 'red':
      return '#ff5252'; // poor
    default:
      return '#9e9e9e'; // unknown / offline
  }
}

export default initPresence;
